import React, { Children, ReactElement, ReactNode } from 'react'
import { RenderedRoute, Route, RoutesTransitions } from './components'
import { Location, RouteObject } from './types'
import { throwIf } from './utils/errors'

export function createRoutesFromChildren(
  children: ReactNode,
  parentPath: number[] = [],
): RouteObject[] {
  let routes: RouteObject[] = []

  Children.forEach(children, (element: any, index) => {
    if (!React.isValidElement(element)) return
    const elProps = element.props as any

    let treePath = [...parentPath, index]
    if (element.type === React.Fragment)
      routes = [
        ...routes,
        ...createRoutesFromChildren(elProps.children, treePath),
      ]

    throwIf(
      element.type !== Route && element.type !== RoutesTransitions,
      `[${
        typeof element.type === 'string' ? element.type : element.type.name
      }] is not a <Route> component. All component children of <Routes> must be a <Route>, <React.Fragment> or <RoutesTransitions>`,
    )

    routes.push({
      index,
      element: elProps.element,
      name: elProps.name,
      keepMountedOnExit: elProps.keepMountedOnExit,
      routeTransitions: elProps.routeTransitions,
      children: elProps.children
        ? createRoutesFromChildren(elProps.children, treePath)
        : undefined,
    })
  })

  return routes
}

export interface RouteBranch {
  routes: RouteObject[]
  location: Location
}

export function flattenRoutes(
  routes: RouteObject[],
  branches: RouteBranch[] = [],
  ancestorsRoutes: RouteObject[] = [],
  parentLocation: Location = '',
) {
  const flattenRoute = (route: RouteObject) => {
    if (!route.name) return

    const branchRoutesSoFar = ancestorsRoutes.concat(route)
    const branchLocationSoFar = parentLocation
      ? `${parentLocation}/${route.name}`
      : route.name

    if (route.children && route.children.length > 0)
      flattenRoutes(
        route.children,
        branches,
        branchRoutesSoFar,
        branchLocationSoFar,
      )

    branches.push({
      routes: branchRoutesSoFar,
      location: branchLocationSoFar,
    })
  }

  for (let route of routes) flattenRoute(route)
  return branches
}

function doesBranchMatchLocation(
  branchLocation: Location,
  routerLocation: Location,
) {
  if (!branchLocation || !routerLocation) return true

  const branchLocationArray = branchLocation.split('/')
  const routerLocationArray = routerLocation.split('/')
  if (routerLocationArray.length < branchLocationArray.length) return false
  if (branchLocationArray[0] !== routerLocationArray[0]) return false

  return doesBranchMatchLocation(
    branchLocationArray.slice(1).join('/'),
    routerLocationArray.slice(1).join('/'),
  )
}

export function matchBranchesWithLocation(
  branches: RouteBranch[],
  location: Location,
) {
  const matchedBranches = branches.filter(branch =>
    doesBranchMatchLocation(branch.location, location),
  )

  // TODO: Optimize algorithm with reduce
  return matchedBranches.sort(
    (a, b) => b.location.split('/').length - a.location.split('/').length,
  )[0]
}

export function renderBranch(
  branch: RouteBranch,
  routerLocation: Location = '',
  ancestorsLocation: Location,
) {
  return branch.routes.reduceRight((outlet, route, index) => {
    const locationSoFar = branch.location
      ? branch.location
          .split('/')
          .slice(0, index + 1)
          .join('/')
      : ''

    const remainedLocation = routerLocation
      ? routerLocation
          .split('/')
          .slice(branch.location.split('/').length)
          .join('/')
      : ''

    const completeLocationSoFar = ancestorsLocation
      ? `${ancestorsLocation}/${locationSoFar}`
      : locationSoFar

    const matchedChildRouteIndex =
      index < branch.routes.length - 1 ? branch.routes[index + 1].index : -1

    const branchRemainedPart = branch.routes.slice(index + 1)

    const newOutlet = route.routeTransitions ? (
      <RoutesTransitions
        activeKey={matchedChildRouteIndex}
        branchRemainedPart={branchRemainedPart}
        {...route.routeTransitions}
      >
        {outlet}
      </RoutesTransitions>
    ) : (
      outlet
    )

    const children: ReactElement = (route.element as ReactElement) || newOutlet

    return (
      <RenderedRoute
        outlet={newOutlet}
        location={remainedLocation}
        locationSoFar={locationSoFar}
        completeLocationSoFar={completeLocationSoFar}
        children={children}
      />
    )
  }, null as ReactElement | null)
}
